// components/LeadsKanbanBoard.js
// Board view for the merged Leads page. Introducer leads and direct leads
// run genuinely different pipelines (introducer: submitted/approved/rejected
// plus a whole pre-consent verification branch; direct: new/assigned/
// in_progress/converted/lost) — they don't line up stage-for-stage, so
// this groups both into 4 shared macro-stages rather than inventing a false
// unified pipeline. The exact underlying status is still shown on each card
// via its normal stage badge, so nothing is hidden — just grouped coarser
// for the board layout. Tim's call (30 July 2026): one board, simplified
// columns, rather than two separate per-source boards.
//
// Props:
//   items          array — the already-filtered/sorted merged leads (same
//                          shape LeadsPage.jsx uses for its list: uid, id,
//                          source, stage, stageMeta, client_name, urgency,
//                          assigned_broker_id, assigned_broker_name,
//                          created_at, unread_count, introducer_name,
//                          sourceLabel, raw)
//   onSelect       fn    — called with { source, id } to open the same
//                          detail modal the list view uses
//   currentUserId  string

import React from 'react';

const MACRO_STAGE = {
  new: 'new_pending',
  submitted: 'new_pending',
  awaiting_consent: 'new_pending',
  assigned: 'in_review',
  in_progress: 'in_review',
  approved: 'in_review',
  converted: 'converted',
  lost: 'closed',
  rejected: 'closed',
  consent_declined: 'closed',
  consent_expired: 'closed',
};

const MACRO_COLUMNS = [
  { key: 'new_pending', label: 'New / pending', border: '#3b82f6' },
  { key: 'in_review',   label: 'In review',      border: '#f59e0b' },
  { key: 'converted',   label: 'Converted',       border: '#10b981' },
  { key: 'closed',      label: 'Closed / lost',   border: '#6b7280' },
];

const URGENCY_DOT = { hot: '#dc2626', warm: '#d97706', cold: '#2563eb' };

function SourceTag({ source, rawSource }) {
  if (rawSource && rawSource.startsWith('fb_')) {
    return <span className="eq-badge" style={{ background: '#e7f0ff', color: '#0866ff', border: '1px solid #b6d0ff' }}>📘 Facebook</span>;
  }
  return source === 'introducer'
    ? <span className="eq-badge" style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' }}>🤝 Introducer</span>
    : <span className="eq-badge" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>📋 Direct</span>;
}

function AssignBadge({ item, currentUserId }) {
  if (item.assigned_broker_id && item.assigned_broker_id === currentUserId) {
    return <span className="eq-badge" style={{ background: '#eef2ff', color: '#4338ca' }}>🎯 Assigned to you</span>;
  }
  if (item.assigned_broker_name) {
    return <span className="eq-badge" style={{ background: '#f1f5f9', color: '#475569' }}>→ {item.assigned_broker_name}</span>;
  }
  return <span className="eq-badge" style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' }}>Unassigned</span>;
}

function LeadCard({ item, onSelect, currentUserId }) {
  return (
    <div
      onClick={() => onSelect({ source: item.source, id: item.id })}
      style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px',
        marginBottom: 10, cursor: 'pointer', position: 'relative', paddingLeft: 18,
        transition: 'border-color 0.15s, box-shadow 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#0e1b2c'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <span style={{ position: 'absolute', left: 8, top: 14, bottom: 14, width: 4, borderRadius: 2,
        background: URGENCY_DOT[item.urgency] || '#d1d5db' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color: '#0e1b2c', fontSize: 14, lineHeight: 1.3 }}>
          {item.client_name || 'Client details hidden'}
        </span>
        {item.unread_count > 0 && (
          <span className="eq-badge" style={{ background: '#ef4444', color: '#fff', whiteSpace: 'nowrap' }}>💬 {item.unread_count}</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <SourceTag source={item.source} rawSource={item.raw?.source} />
        <span className="eq-badge" style={{ background: item.stageMeta.bg, color: item.stageMeta.colour }}>{item.stageMeta.label}</span>
      </div>

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        {item.source === 'introducer' ? `via ${item.introducer_name || 'introducer'}` : item.sourceLabel}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <AssignBadge item={item} currentUserId={currentUserId} />
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </span>
      </div>
    </div>
  );
}

export default function LeadsKanbanBoard({ items, onSelect, currentUserId }) {
  const grouped = { new_pending: [], in_review: [], converted: [], closed: [] };
  items.forEach(it => {
    const key = MACRO_STAGE[it.stage] || 'new_pending';
    (grouped[key] = grouped[key] || []).push(it);
  });

  return (
    <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
      {MACRO_COLUMNS.map(col => {
        const colItems = grouped[col.key] || [];
        return (
          <div key={col.key} style={{ minWidth: 260, width: 260, flexShrink: 0 }}>
            <div style={{ borderTop: `3px solid ${col.border}`, background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: '10px 10px 0 0', padding: '10px 14px', marginBottom: 10,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: '#0e1b2c', fontSize: 13 }}>{col.label}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>{colItems.length}</span>
            </div>

            {colItems.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 4px' }}>No leads</div>
            ) : (
              colItems.map(item => <LeadCard key={item.uid} item={item} onSelect={onSelect} currentUserId={currentUserId} />)
            )}
          </div>
        );
      })}
    </div>
  );
}
